import type { Agency } from "../interfaces/Agency";
import type Fee from "../interfaces/Fee";

/** Campos obligatorios para poder marcar un paquete como entregado. */
export type CampoEntrega = "peso" | "tarifa";

/** Subconjunto de un paquete necesario para validar la entrega. */
export interface PaqueteEntrega {
  peso?: { monto?: number | null; unidad?: string } | null;
  tarifa?: Fee | null;
}

/** Indica si un estado cuenta como entregado segun `agencia.estadosEntregado`. */
export const esEstadoEntregado = (
  agencia: Agency | null | undefined,
  estado?: string | null
): boolean => !!estado && (agencia?.estadosEntregado ?? []).includes(estado);

const tienePeso = (paquete: PaqueteEntrega): boolean =>
  typeof paquete.peso?.monto === "number" && paquete.peso.monto > 0;

const tieneTarifa = (paquete: PaqueteEntrega): boolean =>
  !!paquete.tarifa && typeof paquete.tarifa.monto === "number";

/**
 * Devuelve los campos que faltan para marcar el paquete como entregado.
 * Un arreglo vacio significa que el paquete puede entregarse.
 */
export const camposFaltantesParaEntrega = (
  paquete: PaqueteEntrega
): CampoEntrega[] => {
  const faltantes: CampoEntrega[] = [];
  if (!tienePeso(paquete)) faltantes.push("peso");
  if (!tieneTarifa(paquete)) faltantes.push("tarifa");
  return faltantes;
};

/**
 * Igual que `camposFaltantesParaEntrega`, pero solo aplica cuando el estado
 * indicado cuenta como entregado para la agencia.
 */
export const camposFaltantesSegunEstado = (
  agencia: Agency | null | undefined,
  estado: string | null | undefined,
  paquete: PaqueteEntrega
): CampoEntrega[] =>
  esEstadoEntregado(agencia, estado) ? camposFaltantesParaEntrega(paquete) : [];

/** Texto legible de los campos faltantes: "el peso y la tarifa". */
export const describirCamposFaltantes = (faltantes: CampoEntrega[]): string => {
  const nombres = faltantes.map((campo) =>
    campo === "peso" ? "el peso" : "la tarifa"
  );
  return nombres.join(" y ");
};

/** Mensaje de error mostrado al intentar entregar un paquete incompleto. */
export const mensajeEntregaIncompleta = (faltantes: CampoEntrega[]): string =>
  `Debe registrar ${describirCamposFaltantes(
    faltantes
  )} antes de marcar el paquete como entregado`;
